import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Search, Filter, Package, Activity, Shield, ChevronRight, RefreshCcw, AlertCircle
} from 'lucide-react';
import PageHeader from '../shared/PageHeader';
import StatusBadge from '../shared/StatusBadge';
import ScorePill from '../shared/ScorePill';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AppInventory = () => {
  const [apps, setApps] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [healthFilter, setHealthFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      let url = `${BACKEND_URL}/api/appsupport/inventory?page_size=50`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (healthFilter !== 'all') url += `&health_state=${healthFilter}`;
      const res = await fetch(url);
      const d = await res.json();
      setApps(d.applications || []);
      setTotal(d.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInventory(); }, [statusFilter, healthFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchInventory();
  };

  const openDetail = async (app) => {
    setSelectedApp(app);
    setDetailOpen(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/appsupport/inventory/${app.application_id}`);
      const d = await res.json();
      setDetailData(d);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="application-inventory">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <PageHeader
          title="Application Inventory"
          subtitle={`${total} applications registered`}
          onRefresh={fetchInventory}
        />

        <div className="grid grid-cols-12 gap-4">
          {/* Filters */}
          <div className="col-span-12 lg:col-span-3">
            <Card className="sticky top-4">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Filter className="h-4 w-4" /> Filters
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-4">
                <form onSubmit={handleSearch}>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search apps..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-8 h-9 text-sm"
                      data-testid="filters-search-input"
                    />
                  </div>
                </form>
                <Separator />
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground">Intake Status</label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="h-9 text-sm" data-testid="filters-status-select">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="ACTIVE_MONITORING">Active Monitoring</SelectItem>
                      <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                      <SelectItem value="SLA_PENDING">SLA Pending</SelectItem>
                      <SelectItem value="APPROVAL_PENDING">Approval Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground">Health State</label>
                  <Select value={healthFilter} onValueChange={setHealthFilter}>
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="All states" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All states</SelectItem>
                      <SelectItem value="HEALTHY">Healthy</SelectItem>
                      <SelectItem value="DEGRADED">Degraded</SelectItem>
                      <SelectItem value="CRITICAL">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" size="sm" className="w-full" onClick={() => { setStatusFilter('all'); setHealthFilter('all'); setSearchTerm(''); }} data-testid="filters-reset-button">
                  Reset Filters
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Table */}
          <div className="col-span-12 lg:col-span-9">
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-6 space-y-3">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="registry-table">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Application</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Health</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Supportability</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Status</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Owner</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Signals</th>
                          <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {apps.map((app) => (
                          <tr key={app.application_id} className="hover:bg-muted/50 transition-colors duration-150" data-testid="registry-table-row">
                            <td className="px-4 py-2.5">
                              <div className="text-sm font-medium text-foreground">{app.name}</div>
                              <div className="text-xs text-muted-foreground font-mono">{app.technology_stack?.join(', ')}</div>
                            </td>
                            <td className="px-4 py-2.5">
                              <ScorePill score={app.health_score} data-testid="application-inventory-health-score" />
                            </td>
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                <ScorePill score={app.supportability_score} />
                                <span className="text-xs text-muted-foreground">{app.supportability_label?.replace(/_/g, ' ')}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2.5"><StatusBadge status={app.intake_status} /></td>
                            <td className="px-4 py-2.5 text-xs text-muted-foreground">{app.owning_team}</td>
                            <td className="px-4 py-2.5">
                              <span className="text-xs font-medium text-foreground">{app.active_signal_types?.length || 0}/10</span>
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              <Button variant="ghost" size="sm" onClick={() => openDetail(app)} data-testid="inventory-detail-button">
                                <ChevronRight className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>
              {selectedApp?.name}
            </DialogTitle>
          </DialogHeader>
          {detailData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 rounded-lg bg-muted/50">
                  <div className="text-xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>{detailData.health_score}</div>
                  <div className="text-xs text-muted-foreground">Health Score</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-muted/50">
                  <div className="text-xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>{detailData.supportability_score}</div>
                  <div className="text-xs text-muted-foreground">Supportability</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-muted/50">
                  <div className="text-xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>{detailData.coverage_percentage}%</div>
                  <div className="text-xs text-muted-foreground">Signal Coverage</div>
                </div>
              </div>
              <Separator />
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Details</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Team:</span> {detailData.owning_team}</div>
                  <div><span className="text-muted-foreground">Contact:</span> {detailData.primary_contact}</div>
                  <div><span className="text-muted-foreground">Stack:</span> {detailData.technology_stack?.join(', ')}</div>
                  <div><span className="text-muted-foreground">Version:</span> <span className="font-mono">{detailData.deployed_version}</span></div>
                  <div><span className="text-muted-foreground">Status:</span> <StatusBadge status={detailData.intake_status} /></div>
                  <div><span className="text-muted-foreground">Health:</span> <StatusBadge status={detailData.health_state} /></div>
                </div>
              </div>
              {detailData.signal_coverage && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Signal Coverage</h4>
                    <div className="grid grid-cols-2 gap-1">
                      {detailData.signal_coverage.map((s) => (
                        <div key={s.signal_type} className="flex items-center gap-2 text-xs py-0.5">
                          <span className={`w-1.5 h-1.5 rounded-full ${s.active ? 'bg-[hsl(var(--signal-good))]' : 'bg-[hsl(var(--status-unknown))]'}`}></span>
                          <span className="text-muted-foreground font-mono">{s.signal_type.replace(/_/g, ' ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
              {detailData.sla_definition && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">SLA Definition</h4>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div><span className="text-muted-foreground">Availability:</span> {detailData.sla_definition.availability_target}%</div>
                      <div><span className="text-muted-foreground">Response:</span> {detailData.sla_definition.response_time_target}ms</div>
                      <div><span className="text-muted-foreground">Error Budget:</span> {detailData.sla_definition.error_rate_budget}%</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-3 p-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppInventory;
